/**
 * Public Benchmarks page
 * =======================
 * Citable industry data points teams use to gut-check their cloud posture.
 * Combines FinOps Foundation / Flexera / Apptio public reports with our
 * own honestly-flagged design-partner observations.
 *
 * Builds the network-effect moat: as we hit ~50 customers we'll add an
 * opt-in anonymised cohort lane to compare you against CARTIEAI peers.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, BookOpen, ExternalLink, BarChart3 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Benchmarks = () => {
  const [items, setItems] = useState([]);
  const [lastUpdated, setLastUpdated] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/benchmarks/list`).then(({ data }) => {
      setItems(data.benchmarks || []);
      setLastUpdated(data.last_updated || '');
      setNote(data.note || '');
    }).finally(() => setLoading(false));
  }, []);

  // Group by category for the strip layout
  const byCategory = items.reduce((acc, b) => {
    (acc[b.category] = acc[b.category] || []).push(b);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="benchmarks-page">
      <nav className="fixed top-0 w-full z-50 bg-slate-900 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-600">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" />Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <Badge className="mb-3 bg-emerald-500/10 text-emerald-300 border-emerald-500/30 tracking-wider">
              <BarChart3 className="w-3 h-3 mr-1" /> CITABLE INDUSTRY DATA · {lastUpdated || '2026'}
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-3">
              Cloud benchmarks <span className="text-emerald-600">worth quoting</span>
            </h1>
            <p className="text-slate-200 max-w-2xl text-base sm:text-lg leading-relaxed">
              The numbers we use ourselves when we walk into a room. Public sources are linked so you can verify; our own design-partner observations are clearly flagged. {note || ''}
            </p>
            <div className="flex flex-wrap gap-3 mt-5">
              <Link to="/bill-score">
                <Button className="bg-emerald-500 hover:bg-emerald-400 text-white" data-testid="cta-bill-score">
                  Take the Bill Score quiz to see where you sit
                </Button>
              </Link>
              <Link to="/savings-calculator">
                <Button variant="outline" className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">
                  Run the Savings Calculator
                </Button>
              </Link>
            </div>
          </div>

          {loading ? (
            <p className="text-slate-300">Loading benchmarks…</p>
          ) : (
            <div className="space-y-8">
              {Object.entries(byCategory).map(([category, list]) => (
                <section key={category}>
                  <h2 className="text-lg font-bold text-white mb-3">{category}</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {list.map((b) => {
                      const isInternal = b.source.toLowerCase().startsWith('internal');
                      return (
                        <Card key={b.key} className="bg-slate-900/60 border border-white/10 shadow-sm border-white/10 hover:border-emerald-500/30 transition-colors" data-testid={`bench-${b.key}`}>
                          <CardHeader>
                            <CardTitle className="text-white text-base leading-snug">{b.headline}</CardTitle>
                            {isInternal && (
                              <Badge variant="outline" className="self-start border-emerald-500/40 bg-emerald-500/10 text-cyan-300 text-[11px] mt-1">
                                Internal · CARTIEAI
                              </Badge>
                            )}
                          </CardHeader>
                          <CardContent className="text-sm text-slate-200 space-y-3">
                            <p>{b.explainer}</p>
                            <p className="text-xs text-emerald-300 italic">{b.your_position_hint}</p>
                            <div className="flex items-center justify-between pt-2 border-t border-white/5 text-xs text-slate-300">
                              <span>{b.source}</span>
                              {b.source_url && (
                                <a href={b.source_url} target="_blank" rel="noreferrer" className="hover:text-emerald-300 inline-flex items-center gap-1">
                                  Source <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}

          <Card className="mt-10 bg-gradient-to-br from-emerald-500/5 to-transparent border-emerald-500/20">
            <CardHeader>
              <CardTitle className="text-base text-white flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-emerald-300" />Coming next: anonymised cohort lane
              </CardTitle>
              <CardDescription>The network-effect roadmap, in plain language.</CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-slate-200 space-y-2">
              <p>
                Around customer #50, we'll add a third "lane" to every benchmark: the median across opt-in CARTIEAI customers. It's the only way to answer "how do <em>I</em> compare to teams my size, with my workload mix?" — and it's what we wish existed when we were running cloud bills ourselves.
              </p>
              <p className="text-xs text-slate-300 italic pt-2 border-t border-white/5">
                Privacy: cohort numbers are aggregated, anonymised and require a minimum of 5 contributors per cell. No customer data leaves their tenant. Customers can opt out at any time and their data is removed from the cohort within 30 days.
              </p>
            </CardContent>
          </Card>
        </div>
      </main>

      <footer className="py-6 bg-slate-950 border-t border-white/5 text-center text-xs text-slate-300">
        © 2026 CARTIEAI · <Link to="/privacy-policy" className="hover:text-slate-200">Privacy</Link> · <Link to="/terms" className="hover:text-slate-200">Terms</Link>
      </footer>
    </div>
  );
};

export default Benchmarks;
